import { useEffect } from 'react'
import { products, type ProductId } from './data/products'
import { documents } from './data/documents'
import story from './data/story.html?raw'
import { Home } from './components/Home'
import { ProductPage } from './components/ProductPage'
import { SupportPage } from './components/SupportPage'
import { DocumentPage } from './components/DocumentPage'
import { SiteHeader } from './components/SiteHeader'
import { SiteFooter } from './components/SiteFooter'
import { getLocale, isLocale, localizeNode, localizePath, localePreferenceKey, pageMetadataEnglish, stripLocale, translate, translateHtml } from './i18n/locales'

const routeFromPath = () => stripLocale(window.location.pathname).replace(/^\/+|\/+$/g, '').replace(/\.html$/, '').replace(/^index$/, '')
function setMeta(name: string, content: string, property = false) {
 const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
 let element = document.head.querySelector<HTMLMetaElement>(selector)
 if (!element) { element = document.createElement('meta'); if (property) element.setAttribute('property', name); else element.name = name; document.head.append(element) }
 element.content = content
}

export default function App() {
 const locale = getLocale()
 const slug = routeFromPath()
 const product = Object.hasOwn(products, slug) ? products[slug as ProductId] : undefined
 const basePath = slug ? `/${slug}` : '/'
 const canonical = localizePath(basePath, locale)
 useEffect(() => {
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0]
  const explicitlyLocalized = isLocale(firstSegment)
  if (!explicitlyLocalized) {
   try {
    const remembered = window.localStorage.getItem(localePreferenceKey)
    if (remembered && remembered !== 'en' && isLocale(remembered)) {
     window.location.replace(localizePath(`${window.location.pathname}${window.location.search}${window.location.hash}`, remembered))
     return
    }
   } catch { /* Storage can be disabled by the browser. */ }
  }
  document.documentElement.lang = locale
  const meta = pageMetadataEnglish[basePath as keyof typeof pageMetadataEnglish] ?? pageMetadataEnglish['/']
  document.title = translate(locale, meta[0])
  setMeta('description', translate(locale, meta[1]))
  const canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (canonicalLink) canonicalLink.href = `https://check-in.fit${canonical}`
  setMeta('og:title', translate(locale, meta[0]), true)
  setMeta('og:description', translate(locale, meta[1]), true)
  setMeta('og:url', `https://check-in.fit${canonical}`, true)
  setMeta('og:locale', locale.replace('-', '_'), true)

  const legacy = () => {
   const hash = window.location.hash
   if (!slug && (hash === '#download' || hash === '#family')) { window.location.replace(localizePath('/#apps', locale)); return true }
   if (slug === 'scales-privacy' || slug === 'macros-privacy' || slug === 'physique-privacy') {
    window.location.replace(localizePath(`/privacy#${slug.replace('-privacy', '')}`, locale)); return true
   }
   if (product && (hash === '#privacy' || hash === '#support')) {
    window.location.replace(localizePath(hash === '#privacy' ? `/privacy#${product.id}` : `/support?app=${product.id}`, locale)); return true
   }
   return false
  }
  if (legacy()) return
  const align = () => {
   if (legacy()) return
   const id = window.location.hash.slice(1)
   if (id) requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView())
  }
  align(); window.addEventListener('hashchange', align)
  return () => window.removeEventListener('hashchange', align)
 }, [slug, product, locale, basePath, canonical])
 let content
 if (product) content = <ProductPage product={product} />
 else if (slug === '') content = <Home />
 else if (slug === 'support') content = <SupportPage />
 else if (slug === 'privacy' || slug === 'terms' || slug === 'security') content = <DocumentPage document={documents[slug]} />
 else if (slug === 'creators-story') content = localizeNode(locale, <main className="shell creator-page" id="main-content" tabIndex={-1}><header><h1>Why I made Check-In.</h1><p className="lead">A personal story by Bryan McNamara.</p></header><article className="document-body" dangerouslySetInnerHTML={{ __html: translateHtml(locale, story) }} /></main>)
 else if (/^(scales|macros|physique)-privacy$/.test(slug)) content = localizeNode(locale, <main id="main-content" className="shell not-found"><h1>Our shared privacy policy.</h1><a href={localizePath('/privacy', locale)}>Continue to privacy</a></main>)
 else content = localizeNode(locale, <main id="main-content" className="shell not-found" tabIndex={-1}><p>404 · Page not found</p><h1>Let’s get you<br />back on track.</h1><p>This page may have moved. You can find every app and our shared pages from home.</p><a className="button button-primary" href={localizePath('/', locale)}>Back to home</a><a className="inline-link" href={localizePath('/support', locale)}>Get support</a></main>)
 return <><SiteHeader current={canonical} />{content}<SiteFooter /></>
}
